<script>
  import { createEventDispatcher, onMount } from "svelte";
  import backend from "../../../../../server";
  import { getClientFilterCodes } from "../../../../../js/credentialsApi";
  import Dropdown from "../../../ui/Dropdown.svelte";

  export let provider = "";
  export let api = {};
  export let icons = {};
  export async function refresh(campaignId) {
    await load();
    if (campaignId) {
      return campaigns.find((c) => c.campaignId === campaignId) || null;
    }
    return null;
  }

  const dispatch = createEventDispatcher();

  let isLoading = false;
  let campaigns = [];
  let filteredCampaigns = [];
  let page = 1;
  let pageSize = 10;
  let search = "";
  let typeFilter = "";
  let statusFilter = "";
  let selectedClient = "";
  let allowedClientCodes = [];
  let isSyncing = false;
  let syncMessage = "";

  let configCampaign = null;
  let configOnlyFirstDeposit = false;
  let configMinDepositAmount = null;
  let configSaving = false;
  let configDeleting = false;

  function openConfig(c) {
    configCampaign = c;
    const cfg = c.campaignConfig || {};
    configOnlyFirstDeposit = cfg.onlyFirstDeposit || false;
    configMinDepositAmount = cfg.minDepositAmount || null;
  }

  function closeConfig() {
    configCampaign = null;
    configSaving = false;
    configDeleting = false;
  }

  async function saveConfig() {
    if (!configCampaign || configSaving) return;
    const payload = {
      onlyFirstDeposit: configOnlyFirstDeposit,
      minDepositAmount: configMinDepositAmount || null,
    };
    configSaving = true;
    try {
      await backend.freespin.updateFirst2CampaignConfig(configCampaign.campaignId, payload);
      await load();
      closeConfig();
    } catch (err) {
      console.error("Error saving config:", err);
      alert("Error al guardar la configuración");
      configSaving = false;
    }
  }

  async function deleteConfig() {
    if (!configCampaign || configDeleting) return;
    if (!confirm("¿Eliminar la configuración de esta campaña?")) return;
    configDeleting = true;
    try {
      await backend.freespin.clearFirst2CampaignConfig(configCampaign.campaignId);
      await load();
      closeConfig();
    } catch (err) {
      console.error("Error clearing config:", err);
      alert("Error al eliminar la configuración");
      configDeleting = false;
    }
  }

  let visibleClients = [];
  let sessionClientLocked = [];

  $: {
    filteredCampaigns = (campaigns || []).filter((c) => {
      const needle = search.toLowerCase();
      const matchesSearch =
        !needle ||
        (c.campaignName || "").toLowerCase().includes(needle) ||
        (c.campaignId || "").toLowerCase().includes(needle);
      const matchesType = !typeFilter || c.providerCampaignType === typeFilter;
      const matchesStatus =
        !statusFilter || getStatusInfo(c).label === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
    const totalPages = Math.max(1, Math.ceil(filteredCampaigns.length / pageSize));
    if (page > totalPages) page = totalPages;
  }

  $: pagedCampaigns = filteredCampaigns.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  $: pagerInfo = {
    total: filteredCampaigns.length,
    from: filteredCampaigns.length === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, filteredCampaigns.length),
  };

  async function loadClients() {
    try {
      const allowedCodes = await getClientFilterCodes();
      allowedClientCodes = Array.isArray(allowedCodes) ? allowedCodes : [];

      if (allowedCodes === null) {
        const res = await backend.client.lists({ page: 1, xpage: 300, summary: true });
        visibleClients = res?.data?.list || [];
        selectedClient = "";
        sessionClientLocked = false;
        allowedClientCodes = [];
        return;
      }

      if (allowedCodes.length) {
        const res = await backend.client.lists({ page: 1, xpage: 300, summary: true, codes: allowedCodes.join(",") });
        visibleClients = res?.data?.list || [];
      } else {
        visibleClients = [];
      }

      let data = {};
      try { data = JSON.parse(sessionStorage.getItem("user") || "{}"); } catch {}
      const groupCode = data?.client_code ?? data?.clientCode ?? null;
      if (groupCode && allowedCodes.length === 1) {
        selectedClient = allowedCodes[0];
        sessionClientLocked = true;
      } else {
        selectedClient = "";
        sessionClientLocked = false;
      }
    } catch (err) {
      console.error("Error loading clients:", err);
    }
  }

  function getClientName(clientCode) {
    const found = visibleClients.find((vc) => vc.code === clientCode);
    return found?.namesite || clientCode || "—";
  }

  async function load() {
    if (!provider) return;
    if (!selectedClient) { campaigns = []; return; }
    isLoading = true;
    try {
      const params = { clientCode: selectedClient };
      const res = await api.listByProvider(provider, params);
      campaigns = res?.data || [];
    } catch (err) {
      console.error("Error loading FIRST v2 campaigns:", err);
      alert("Error al cargar campañas de FIRST v2");
    } finally {
      isLoading = false;
    }
  }

  async function syncCampaigns() {
    if (!selectedClient) {
      alert("Selecciona un cliente para sincronizar");
      return;
    }
    isSyncing = true;
    syncMessage = "";
    try {
      const res = await api.syncCampaigns(selectedClient);
      const data = res?.data || {};
      syncMessage = `Sincronizado: ${data.depositSynced || 0} depósitos, ${data.freebetSynced || 0} freebets`;
      await load();
    } catch (err) {
      console.error("Error syncing FIRST v2 campaigns:", err);
      alert("Error al sincronizar campañas");
    } finally {
      isSyncing = false;
    }
  }

  function getTypeInfo(type) {
    if (type === "DEPOSIT") return { label: "Depósito", class: "type-deposit" };
    if (type === "FREEBET") return { label: "FreeBet", class: "type-freebet" };
    return { label: "—", class: "text-muted" };
  }

  function creationInstant(c) {
    const t = c.firstCreatedAt || c.created;
    const d = new Date(t);
    return isNaN(d) ? 0 : d.getTime();
  }

  function getStatusInfo(c) {
    const s = Number(c.campaignStatus);
    if (s === 1) return { label: "Activo", class: "status-active" };
    if (s === 0) return { label: "Desactivado", class: "status-inactive" };
    return { label: "—", class: "status-inactive" };
  }

  function fmtDate(v) {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? "—" : d.toLocaleDateString();
  }

  function expType(c) {
    return c.uExpirationTimeType || c.uexpirationTimeType || null;
  }
  function expDays(c) {
    return c.uDaysTillExpiration ?? c.udaysTillExpiration ?? null;
  }
  function getExpiry(c) {
    return c.firstExpiredAt || null;
  }

  function playerCount(c) {
    return (c.playerList || []).filter((p) =>
      (p.deliveredPromos || []).some((promo) => promo.status === 1)
    ).length;
  }

  let selectCampaignPending = null;
  let isSelecting = false;

  async function handleSelect(c) {
    if (c.isActiveForClient === true) {
      if (!confirm("¿Quitar el marcado de esta campaña como automática?")) return;
      isSelecting = true;
      try {
        await api.unselectCampaign(c.campaignId);
        await load();
      } catch (err) {
        console.error("Error unselecting campaign:", err);
        alert("Error al quitar la campaña como automática");
      } finally {
        isSelecting = false;
      }
      return;
    }

    const s = Number(c.campaignStatus);
    if (s !== 1) {
      alert("La campaña está inactiva, no se puede marcar como automática");
      return;
    }
    if (!c.campaignConfig) {
      alert("La campaña no tiene configuración. Configúrala primero para poder marcarla como automática");
      return;
    }

    const sameType = (campaigns || []).filter(
      (x) =>
        x.client?.clientCode === c.client?.clientCode &&
        x.providerCampaignType === c.providerCampaignType &&
        x.isActiveForClient === true
    );
    const toReplace = sameType.filter((x) => x.campaignId !== c.campaignId);
    selectCampaignPending = { campaign: c, toReplace };
  }

  async function confirmSelect() {
    if (!selectCampaignPending) return;
    isSelecting = true;
    try {
      await api.selectCampaign(selectCampaignPending.campaign.campaignId);
      await load();
    } catch (err) {
      console.error("Error selecting campaign:", err);
      alert("Error al marcar la campaña como automática");
    } finally {
      isSelecting = false;
      selectCampaignPending = null;
    }
  }

  onMount(() => {
    loadClients().then(() => load());
  });
</script>

<div class="wrapp">
  <div class="menu-filter">
    <div class="row-top">
      <div class="client-filter f-item">
        <span class="field-label">Cliente</span>
        <Dropdown
                options={visibleClients}
                bind:value={selectedClient}
                placeholder="Todos"
                valueKey="code"
                labelKey="namesite"
                isStructuredDescription={true}
                paramStructuredName={["code", "namesite"]}
                on:change={load}
        />
      </div>

      <div class="filters-row-quick">
        <div class="f-item">
          <label for="searchV2">Campaña</label>
          <input
            id="searchV2"
            class="form-control"
            placeholder="Nombre o ID"
            bind:value={search}
          />
        </div>
        <div class="f-item">
          <label for="typeFilterV2">Tipo</label>
          <select id="typeFilterV2" class="form-select" bind:value={typeFilter}>
            <option value="">Todos</option>
            <option value="DEPOSIT">Depósito</option>
            <option value="FREEBET">FreeBet</option>
          </select>
        </div>
        <div class="f-item">
          <label for="statusFilterV2">Estado</label>
          <select id="statusFilterV2" class="form-select" bind:value={statusFilter}>
            <option value="">Todos</option>
            <option value="1">Activo</option>
            <option value="0">Desactivado</option>
          </select>
        </div>
      </div>

      <div class="actions-col">
        <button
          class="btn btn-pink btn-sm"
          on:click={syncCampaigns}
          disabled={isSyncing}
        >
          {isSyncing ? "Sincronizando..." : "Sincronizar con FIRST"}
        </button>
      </div>
    </div>

    {#if syncMessage}
      <div class="alert alert-info mt-2 mb-0 py-2">{syncMessage}</div>
    {/if}
    <div class="text-muted small mt-2 mb-0">
      Solo se puede tener una campaña en modo automático de cada tipo por cliente.
    </div>
  </div>
</div>

{#if isLoading}
  <p>Cargando…</p>
{:else if !selectedClient}
  <p class="text-center text-muted">Seleccione un cliente para ver las campañas.</p>
{:else if pagedCampaigns.length === 0}
  <p class="text-center text-muted">No se encontraron campañas sincronizadas.</p>
{:else}
  <div class="table-wrapper">
    <table class="table align-middle mb-0">
      <thead>
        <tr>
          <th>Cliente</th>
          <th>Campaña</th>
          <th>Tipo</th>
          <th>Monto</th>
          <th>Moneda</th>
          <th>Estado</th>
          <th>Detalles</th>
          <th>Jugadores</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {#each pagedCampaigns as c}
          <tr>
            <td>{getClientName(c.client?.clientCode)}</td>
            <td>
              <span class="d-block text-truncate" style="max-width: 250px;" title={c.campaignName}>
                {c.campaignName || "—"}
              </span>
              <small class="text-muted">{c.campaignId}</small>
            </td>
            <td>
              <span class={`badge rounded-pill ${getTypeInfo(c.providerCampaignType).class}`}>
                {getTypeInfo(c.providerCampaignType).label}
              </span>
            </td>
            <td>
              {#if c.providerCampaignType === "FREEBET" && c.slotConfigs?.[0]?.amountPerRound}
                <span class="fw-bold">{c.slotConfigs[0].amountPerRound}</span>
              {:else if c.providerCampaignType === "DEPOSIT"}
                {@const bonusPromo = (c.playerList || []).flatMap(p => (p.deliveredPromos || [])).find(pr => pr.status === 1 && pr.depositAmount > 0 && pr.bonusAmount > 0)}
                {#if bonusPromo}
                  {@const pct = (bonusPromo.bonusAmount / bonusPromo.depositAmount * 100).toFixed(0)}
                  <span class="fw-bold">{pct}%</span>
                {:else}
                  <span class="text-muted">—</span>
                {/if}
              {:else}
                <span class="text-muted">—</span>
              {/if}
            </td>
            <td>{c.currency || "—"}</td>
            <td>
              <span class={`badge status-badge rounded-pill ${getStatusInfo(c).class}`}>
                <span class="status-dot"></span>
                {getStatusInfo(c).label}
              </span>
              {#if c.isActiveForClient === true}
                <span class="badge auto-badge rounded-pill">Automática</span>
              {/if}
            </td>
            <td>
              {#if c.campaignConfig}
                <div class="config-preview">
                  <span class="badge config-badge">{c.campaignConfig.onlyFirstDeposit ? "1er depósito" : "Cualquier depósito"}</span>
                  {#if c.campaignConfig.minDepositAmount}
                    <span class="badge config-badge">Min ${c.campaignConfig.minDepositAmount}</span>
                  {/if}
                </div>
              {:else}
                <span class="text-danger" style="font-size:0.8rem; font-weight:600;">Sin configurar</span>
              {/if}
              <div class="text-muted" style="font-size:0.72rem;">
                <span>Creada: {fmtDate(c.firstCreatedAt)}</span>
                {#if getExpiry(c)}
                  <span> · Expira: {fmtDate(getExpiry(c))}</span>
                {:else if expType(c) === "DTE" && expDays(c)}
                  <span> · Vence: {expDays(c)} días</span>
                {/if}
              </div>
            </td>
            <td>{playerCount(c)}</td>
            <td class="actions-cell">
              {#if Number(c.campaignStatus) === 1}
                <button
                  class="btn btn-icon btn-config"
                  title="Configurar"
                  on:click={() => openConfig(c)}
                >
                  <i class="fas fa-cog"></i>
                </button>
              {/if}
              <button
                class={`btn btn-sm ${c.isActiveForClient === true ? "btn-unselect" : "btn-select"}`}
                title={c.isActiveForClient === true ? "Quitar automática" : "Marcar como automática"}
                on:click={() => handleSelect(c)}
                disabled={isSelecting}
              >
                {c.isActiveForClient === true ? "Quitar automática" : "Automática"}
              </button>
              <button
                class="btn btn-icon btn-view"
                title="Ver jugadores"
                on:click={() => {
                  const clientObj = visibleClients.find((vc) => vc.code === c.client?.clientCode);
                  const enriched = { ...c, _resolvedClientId: clientObj?.id || null };
                  dispatch("openplayers", enriched);
                }}
              >
                {@html icons.persons}
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<div class="pagination-bar">
  <div class="pag-left">
    <span class="total-badge">Total: <strong>{pagerInfo.total}</strong></span>
  </div>

  <div class="pag-center">
    <button type="button" class="pager-btn" on:click={() => page = 1} disabled={page === 1}>‹‹</button>
    <button type="button" class="pager-btn" on:click={() => page = Math.max(1, page - 1)} disabled={page === 1}>‹</button>
    <span class="page-info">
      Página <strong>{page}</strong> de <strong>{Math.max(1, Math.ceil(filteredCampaigns.length / pageSize))}</strong>
    </span>
    <button type="button" class="pager-btn" on:click={() => page++} disabled={page >= Math.ceil(filteredCampaigns.length / pageSize)}>›</button>
    <button type="button" class="pager-btn" on:click={() => page = Math.ceil(filteredCampaigns.length / pageSize)} disabled={page >= Math.ceil(filteredCampaigns.length / pageSize)}>››</button>
  </div>

  <div class="pag-right">
    <span class="range-text">{pagerInfo.from}–{pagerInfo.to} de {pagerInfo.total}</span>
    <select class="form-select form-select-sm size-select" bind:value={pageSize} on:change={() => (page = 1)}>
      {#each [10, 25, 50, 100] as opt}
        <option value={opt}>{opt} / pág</option>
      {/each}
    </select>
  </div>
</div>

{#if configCampaign}
  <div class="modal-backdrop fade show"></div>
  <div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.3);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content config-modal">
        <div class="modal-header py-2">
          <div class="modal-title-block">
            <h6 class="modal-title mb-0">Configuración de campaña</h6>
            <div class="modal-campaign-name" title={configCampaign.campaignName}>
              {configCampaign.campaignName || configCampaign.campaignId}
            </div>
          </div>
          <button type="button" class="btn-close" on:click={closeConfig}></button>
        </div>

        <div class="modal-body py-3">
          <div class="campaign-meta">
            <span class={`badge rounded-pill ${getTypeInfo(configCampaign.providerCampaignType).class}`}>
              {getTypeInfo(configCampaign.providerCampaignType).label}
            </span>
            <span class={`badge rounded-pill status-badge ${getStatusInfo(configCampaign).class}`}>
              <span class="status-dot"></span>
              {getStatusInfo(configCampaign).label}
            </span>
            <span class="meta-item"><span class="meta-label">ID</span> {configCampaign.campaignId}</span>
            <span class="meta-item"><span class="meta-label">Moneda</span> {configCampaign.currency || "—"}</span>
            <span class="meta-item"><span class="meta-label">Creada</span> {fmtDate(configCampaign.firstCreatedAt)}</span>
          </div>

          <hr class="my-3" />

          <div class="config-field">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <div>
                <label class="fw-semibold small mb-1">Solo primer depósito</label>
                <div class="text-muted small">Apagado = aplica a cualquier depósito</div>
              </div>
              <div class="form-check form-switch fs-5 mb-0">
                <input class="form-check-input" type="checkbox" bind:checked={configOnlyFirstDeposit} />
              </div>
            </div>
          </div>

          <div class="config-field mt-3">
            <label class="fw-semibold small mb-1">Monto mínimo de depósito</label>
            <div class="input-group input-group-sm">
              <span class="input-group-text">{configCampaign.currency || "USD"}</span>
              <input type="number" class="form-control" bind:value={configMinDepositAmount} min="0" step="0.01" placeholder="Sin mínimo" />
              {#if configMinDepositAmount != null}
                <button class="btn btn-outline-secondary" type="button" title="Quitar mínimo" on:click={() => (configMinDepositAmount = null)}>×</button>
              {/if}
            </div>
            <small class="text-muted">El bono solo aplica si el depósito es mayor o igual a este monto</small>
          </div>
        </div>

        <div class="modal-footer py-2">
          <button class="btn btn-danger btn-sm" on:click={deleteConfig} disabled={configDeleting}>
            {configDeleting ? "Borrando..." : "Borrar configuración"}
          </button>
          <span class="flex-grow-1"></span>
          <button class="btn btn-secondary btn-sm" on:click={closeConfig}>Cancelar</button>
          <button class="btn btn-primary btn-sm" on:click={saveConfig} disabled={configSaving}>
            {configSaving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

{#if selectCampaignPending}
  <div class="modal-backdrop fade show"></div>
  <div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.3);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content config-modal">
        <div class="modal-header py-2">
          <h6 class="modal-title mb-0">Marcar campaña como automática</h6>
          <button type="button" class="btn-close" on:click={() => (selectCampaignPending = null)}></button>
        </div>
        <div class="modal-body py-3">
          <p class="mb-2 small">
            Al marcar <strong>{selectCampaignPending.campaign.campaignName || selectCampaignPending.campaign.campaignId}</strong> como automática,
            esta será la campaña que reciba la asignación automática de este cliente.
          </p>
          {#if selectCampaignPending.toReplace.length > 0}
            <div class="alert alert-warning py-2 px-3 small mb-0">
              Se desactivará como automática la siguiente campaña del mismo tipo ({getTypeInfo(selectCampaignPending.campaign.providerCampaignType).label}):
              <ul class="mb-0 mt-1">
                {#each selectCampaignPending.toReplace as t}
                  <li>{t.campaignName || t.campaignId}</li>
                {/each}
              </ul>
              Solo puede haber una campaña automática activa por tipo y cliente.
            </div>
          {:else}
            <div class="text-muted small">No hay otra campaña automática activa de este tipo para este cliente.</div>
          {/if}
        </div>
        <div class="modal-footer py-2">
          <button class="btn btn-secondary btn-sm" on:click={() => (selectCampaignPending = null)} disabled={isSelecting}>Cancelar</button>
          <button class="btn btn-primary btn-sm" on:click={confirmSelect} disabled={isSelecting}>
            {isSelecting ? "Marcando..." : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .type-freebet { background-color: #e8d5f5; color: #5b21b6; }
  .type-deposit { background-color: #cffafe; color: #155e75; }

  .wrapp { padding: 10px; }
  .menu-filter {
    display: block;
    padding: .75rem;
    background: #fff;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: .5rem;
    margin-bottom: .5rem;
  }
  .row-top {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: .75rem;
    align-items: end;
  }
  .actions-col {
    display: flex;
    align-items: end;
    gap: .5rem;
    flex-wrap: wrap;
  }
  .client-filter {
    width: 300px;
    max-width: 100%;
  }
  .client-filter :global(.trigger) {
    border: 1px solid #ced4da;
  }
  .client-filter :global(.trigger.placeholder) {
    color: #333;
  }
  .filters-row-quick {
    display: grid;
    grid-template-columns: repeat(3, minmax(140px, 1fr));
    gap: .6rem;
    align-items: end;
  }
  .f-item { display: flex; flex-direction: column; }
  .f-item label {
    font-size: .7rem;
    color: #666;
    margin-bottom: .3rem;
    display: block;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .f-item :is(input, select) {
    font-size: 0.85rem;
    padding: .4rem .55rem;
    border: 1px solid var(--bs-border-color, #e2e6ea);
    border-radius: .4rem;
    background: #fff;
    color: #333;
    min-height: 38px;
  }
  .f-item :is(input, select):focus {
    outline: none;
    border-color: #86b7fe;
    box-shadow: 0 0 0 3px rgba(13,110,253,.1);
  }

  .btn-pink {
    background-color: #2a1a28;
    color: white;
    font-size: 0.80rem;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
  .btn-pink:hover { background-color: #FF0090; color: white; }
  .btn-blue {
    background-color: #0d6efd;
    color: white;
    font-size: 0.80rem;
    padding: 0.5rem 1.2rem;
    border-radius: 6px;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  /* Table */
  .table-wrapper {
    overflow: auto;
    height: 500px;
    border-radius: 0.5rem;
    border: 1px solid var(--bs-border-color, #e5e7eb);
  }
  .table-wrapper table { margin-bottom: 0; }
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
    box-shadow: 0 1px 0 #dee2e6;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #555;
    font-weight: 700;
    padding: 0.6rem 0.5rem;
    border-bottom: none;
  }
  tbody tr { transition: background-color 0.12s ease; }
  tbody tr:hover { background-color: #f4f7ff !important; }
  tbody td {
    font-size: 0.85rem;
    padding: 0.35rem 0.4rem;
    vertical-align: middle;
  }

  /* Status badges */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35em 0.75em;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }
  .status-active { background: #d1e7dd; color: #0f5132; }
  .status-inactive { background: #e2e3e5; color: #41464b; }
  .auto-badge {
    background: #fff3cd;
    color: #664d03;
    border: 1px solid #ffca3a;
    font-size: 0.72rem;
    font-weight: 600;
    margin-left: 0.3rem;
  }

  /* Actions */
  .actions-cell {
    display: flex;
    gap: 0.35rem;
    justify-content: center;
    align-items: center;
  }
  .btn-icon {
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid transparent;
    transition: all 0.15s ease;
    background: transparent;
    cursor: pointer;
  }
  .btn-icon :global(svg),
  .btn-icon :global(i) {
    width: 16px;
    height: 16px;
    display: inline-flex;
  }
  .btn-view {
    color: #0d6efd;
    border-color: #cfe2ff;
    background: #f0f7ff;
  }
  .btn-view:hover {
    background: #0d6efd;
    color: #fff;
    border-color: #0d6efd;
  }
  .btn-config {
    color: #6c757d;
    border-color: #dee2e6;
    background: #f8f9fa;
  }
  .btn-config:hover {
    background: #6c757d;
    color: #fff;
    border-color: #6c757d;
  }
  .btn-select {
    color: #0d6efd;
    border-color: #cfe2ff;
    background: #f0f7ff;
    font-size: 0.75rem;
    white-space: nowrap;
  }
  .btn-select:hover {
    background: #0d6efd;
    color: #fff;
    border-color: #0d6efd;
  }
  .btn-unselect {
    color: #dc3545;
    border-color: #f1aeb5;
    background: #fff5f5;
    font-size: 0.75rem;
    white-space: nowrap;
  }
  .btn-unselect:hover {
    background: #dc3545;
    color: #fff;
    border-color: #dc3545;
  }
  .config-preview {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 3px;
    align-items: center;
  }
  .config-badge {
    font-size: 0.68rem;
    font-weight: 500;
    background: #e8f4f8;
    color: #0c5460;
    padding: 0.2em 0.5em;
    border-radius: 4px;
  }

  .config-modal { border-radius: .75rem; overflow: hidden; }
  .config-modal .modal-header { border-bottom: none; }
  .config-modal .modal-footer { border-top: none; background: #f8f9fa; }
  .modal-title-block { display: flex; flex-direction: column; gap: 2px; }
  .modal-campaign-name {
    font-size: 0.8rem;
    font-weight: 600;
    color: #495057;
    max-width: 340px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .campaign-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .5rem;
    font-size: .78rem;
    color: #555;
  }
  .meta-item { display: inline-flex; align-items: center; gap: .35rem; }
  .meta-label {
    text-transform: uppercase;
    letter-spacing: .03em;
    font-size: .66rem;
    color: #999;
    font-weight: 600;
  }
  .config-field {
    padding: .65rem .8rem;
    border: 1px solid var(--bs-border-color, #e9ecef);
    border-radius: .5rem;
    background: #fff;
  }
  .config-field:focus-within { border-color: #86b7fe; box-shadow: 0 0 0 3px rgba(13,110,253,.08); }

  /* Pagination */
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
    padding: .65rem .75rem;
    margin-top: .5rem;
    background: #fff;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: .5rem;
    flex-wrap: wrap;
  }
  .pag-left, .pag-right, .pag-center {
    display: flex;
    align-items: center;
    gap: .5rem;
    min-width: max-content;
  }
  .pag-center {
    flex: 1 1 auto;
    justify-content: center;
    gap: .5rem;
  }
  .total-badge {
    font-size: 0.8rem;
    color: #555;
    background: #f8f9fa;
    border: 1px solid #e5e7eb;
    padding: .35rem .65rem;
    border-radius: 50rem;
  }
  .total-badge strong { color: #111; }
  .page-info {
    font-size: 0.85rem;
    color: #555;
    white-space: nowrap;
  }
  .page-info strong { color: #111; }
  .range-text {
    font-size: 0.8rem;
    color: #777;
    white-space: nowrap;
  }
  .pager-btn {
    --size: 34px;
    width: var(--size);
    height: var(--size);
    min-width: var(--size);
    min-height: var(--size);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    background: #fff;
    border-radius: .4rem;
    cursor: pointer;
    font-size: 0.85rem;
    color: #444;
    transition: all 0.15s ease;
  }
  .pager-btn:hover:not(:disabled) {
    background: #f4f7ff;
    border-color: #b6d4fe;
  }
  .pager-btn:disabled {
    opacity: .45;
    cursor: not-allowed;
    background: #f5f5f5;
  }
  .field-label {
    display: block;
    margin-bottom: 0.3rem;
    color: #666;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
</style>
